import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AppHeader from './AppHeader';
import { useLanguage } from './LanguageContext';
import { el as elStrings, en as enStrings } from './i18n/app';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const MODULES = [
  'Project Charter', 'Kickoff Presentation', 'Risk Management', 'Schedule',
  'Scope / WBS', 'Issues', 'Budget', 'Organization Chart', 'Stakeholders',
  'Documents', 'Daily Log', 'Lessons Learned', 'Transcripts', 'Meetings', 'AI Advisor',
  'Project Specs', 'Change Control', 'Reporting','AI Assistant',
];

// Display-only labels — the underlying module key (used for permission lookups
// against the backend) stays as-is; only the shown text differs.
const MODULE_DISPLAY_LABELS: Record<string, string> = {
  'Daily Log': 'Task Tracker',
};

// Ρόλοι με πλήρη πρόσβαση σε ΟΛΑ τα έργα + modules — δεν χρειάζονται καθόλου tabs
const FULL_ACCESS_ROLES = ['admin', 'pmo'];
// PM: project-scoped (χρειάζεται το tab "Πρόσβαση σε Έργα"), αλλά πλήρη δικαιώματα
// modules μέσα σε αυτά τα έργα — δεν χρειάζεται το tab "Δικαιώματα Modules"
const PROJECT_SCOPED_FULL_MODULES_ROLES = ['pm'];

const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'pm', label: 'Project Manager (PM)' },
  { value: 'pmo', label: 'PMO' },
  { value: 'coordinator', label: 'Project Coordinator' },
  { value: 'member', label: 'Project Member' },
  { value: 'guest_user', label: 'Guest User' },
];

// A PM (not admin/pmo/super_admin) can only offer these roles when inviting/creating users
const PM_ASSIGNABLE_ROLES = ['coordinator', 'member', 'guest_user'];

const ROLE_LABELS: Record<string, string> = {
  admin: 'ADMIN', pm: 'PM', pmo: 'PMO',
  coordinator: 'COORDINATOR', member: 'MEMBER', guest_user: 'GUEST',
};

const ROLE_BADGE_COLORS: Record<string, { bg: string; color: string }> = {
  admin:       { bg: '#ede9fe', color: '#4f46e5' },
  pm:          { bg: '#ede9fe', color: '#4f46e5' },
  pmo:         { bg: '#e0f2fe', color: '#0369a1' },
  coordinator: { bg: '#f0f2f5', color: '#6b7280' },
  member:      { bg: '#f0fdf4', color: '#16a34a' },
  guest_user:  { bg: '#fef3c7', color: '#d97706' },
};

const UserManagement = ({ user, token, projects, onBack, onLogout, onProfileEdit, onPlanLimitReached, onUserUpdated }: {
  user: any; token: string; projects: any[]; onBack: () => void;
  onLogout?: () => void; onProfileEdit?: () => void;
  onPlanLimitReached?: () => void; onUserUpdated?: (updated: any) => void;
}) => {
  const { language } = useLanguage();
  const L = language === 'en' ? enStrings : elStrings;

  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewUser, setShowNewUser] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [deletingUser, setDeletingUser] = useState<any>(null);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [permissions, setPermissions] = useState<any[]>([]);
  const [memberProjects, setMemberProjects] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'projects' | 'modules'>('projects');
  const [newUser, setNewUser] = useState({ email: '', full_name: '', role: 'coordinator', password: '' });
  const [emailCheckResult, setEmailCheckResult] = useState<null | { exists: boolean; already_in_org?: boolean; user?: any }>(null);
  const [emailChecking, setEmailChecking] = useState(false);
  const [inviteProject, setInviteProject] = useState('');
  const [inviteRole, setInviteRole] = useState(user.role === 'pm' ? 'coordinator' : 'pm');
  const inviteRoleOptions = user.role === 'pm' ? ROLE_OPTIONS.filter(r => PM_ASSIGNABLE_ROLES.includes(r.value)) : ROLE_OPTIONS;
  // PM is project-scoped — the admin picks exactly which projects a new/invited PM gets full rights on
  const [pmProjectIds, setPmProjectIds] = useState<string[]>([]);
  const [editForm, setEditForm] = useState({ full_name: '', email: '', role: '' });
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [editError, setEditError] = useState('');
  const [userLimit, setUserLimit] = useState<number | null>(null);

  useEffect(() => { fetchUsers(); }, []);

  useEffect(() => {
    axios.get(`${API}/organizations/my/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setUserLimit(r.data.max_users ?? null))
      .catch(() => {});
  }, [user.org_plan]);

  useEffect(() => {
    if (selectedUser) {
      fetchMemberProjects();
      if (selectedProject) fetchPermissions();
    }
  }, [selectedUser, selectedProject]);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchMemberProjects = async () => {
    if (!selectedUser) return;
    const memberIds: string[] = [];
    await Promise.all(projects.map(async (p) => {
      try {
        const res = await axios.get(`${API}/projects/${p.id}/members`, { headers: { Authorization: `Bearer ${token}` } });
        if ((res.data as string[]).includes(selectedUser.id)) memberIds.push(p.id);
      } catch {}
    }));
    setMemberProjects(memberIds);
  };

  const fetchPermissions = async () => {
    if (!selectedUser || !selectedProject) return;
    try {
      const res = await axios.get(`${API}/users/${selectedUser.id}/permissions/${selectedProject}`, { headers: { Authorization: `Bearer ${token}` } });
      setPermissions(res.data);
    } catch (err) { console.error(err); }
  };

  // Auto-check 700ms after email stops changing (handles typing + autofill via onChange)
  useEffect(() => {
    if (!showNewUser) return;
    if (!newUser.email || !newUser.email.includes('@')) { setEmailCheckResult(null); return; }
    const timer = setTimeout(() => checkEmail(newUser.email), 700);
    return () => clearTimeout(timer);
  }, [newUser.email, showNewUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkEmail = async (email: string) => {
    if (!email || !email.includes('@')) { setEmailCheckResult(null); return; }
    setEmailChecking(true);
    try {
      const res = await axios.get(`${API}/users/check-email?email=${encodeURIComponent(email)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('[checkEmail]', email, '->', res.data);
      setEmailCheckResult(res.data);
    } catch (err: any) {
      console.error('[checkEmail] error:', err?.response?.data ?? err?.message);
      setEmailCheckResult(null);
    }
    finally { setEmailChecking(false); }
  };

  const handleInviteUser = async () => {
    if (inviteRole === 'pm' && pmProjectIds.length === 0) { setError(L.userManagement.errorRequired); return; }
    setCreating(true); setError('');
    try {
      await axios.post(`${API}/users/invite`,
        {
          email: newUser.email, role: inviteRole,
          project_ids: inviteRole === 'pm' ? pmProjectIds : (inviteProject ? [inviteProject] : []),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setShowNewUser(false);
      setEmailCheckResult(null);
      setInviteProject('');
      setPmProjectIds([]);
      setNewUser({ email: '', full_name: '', role: 'coordinator', password: '' });
      fetchUsers();
    } catch (err: any) {
      setError(err?.response?.data?.message ?? L.userManagement.errorInvite);
    } finally { setCreating(false); }
  };

  const handleToggleMembership = async (projectId: string, isMember: boolean) => {
    if (!selectedUser) return;
    try {
      if (isMember) {
        await axios.delete(`${API}/projects/${projectId}/members/${selectedUser.id}`, { headers: { Authorization: `Bearer ${token}` } });
        setMemberProjects(prev => prev.filter(id => id !== projectId));
      } else {
        await axios.post(`${API}/projects/${projectId}/members/${selectedUser.id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
        setMemberProjects(prev => [...prev, projectId]);
      }
    } catch (err) { console.error(err); }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.full_name || !newUser.password) { setError(L.userManagement.errorRequired); return; }
    if (newUser.role === 'pm' && pmProjectIds.length === 0) { setError(L.userManagement.errorRequired); return; }
    setCreating(true); setError('');
    try {
      await axios.post(`${API}/users`,
        { ...newUser, project_ids: newUser.role === 'pm' ? pmProjectIds : undefined },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setNewUser({ email: '', full_name: '', role: 'coordinator', password: '' });
      setPmProjectIds([]);
      setShowNewUser(false);
      fetchUsers();
    } catch (err: any) {
      const msg: string = err?.response?.data?.message ?? '';
      setError(msg || L.userManagement.errorCreate);
    } finally { setCreating(false); }
  };

  const openEditModal = (u: any) => { setEditForm({ full_name: u.full_name, email: u.email, role: u.role }); setEditingUser(u); setEditError(''); };

  const handleUpdateUser = async () => {
    if (!editForm.full_name || !editForm.email || !editForm.role) { setEditError(L.userManagement.errorRequired); return; }
    setSaving(true); setEditError('');
    try {
      const updated = await axios.put(`${API}/users/${editingUser.id}`, editForm, { headers: { Authorization: `Bearer ${token}` } });
      setUsers(prev => prev.map(u => u.id === editingUser.id ? { ...u, ...updated.data } : u));
      if (selectedUser?.id === editingUser.id) setSelectedUser({ ...selectedUser, ...updated.data });
      if (user.id === editingUser.id) onUserUpdated?.({ ...user, ...updated.data });
      setEditingUser(null);
    } catch { setEditError(L.userManagement.errorUpdate); }
    finally { setSaving(false); }
  };

  const handleDeleteUser = async () => {
    if (!deletingUser) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/users/${deletingUser.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (selectedUser?.id === deletingUser.id) setSelectedUser(null);
      setDeletingUser(null);
      fetchUsers();
    } catch (err) { console.error(err); }
    finally { setDeleting(false); }
  };

  const handlePermissionChange = async (module: string, type: 'can_view' | 'can_edit' | 'can_delete', value: boolean) => {
    const current = permissions.find(p => p.module === module) || { can_view: false, can_edit: false, can_delete: false };
    let updated = { ...current, [type]: value };
    if (type === 'can_edit' && value) updated.can_view = true;
    if (type === 'can_delete' && value) updated.can_view = true;
    if (type === 'can_view' && !value) { updated.can_edit = false; updated.can_delete = false; }
    try {
      await axios.post(`${API}/users/${selectedUser.id}/permissions/${selectedProject}`, { module, ...updated }, { headers: { Authorization: `Bearer ${token}` } });
      fetchPermissions();
    } catch (err) { console.error(err); }
  };

  const getPermission = (module: string) => permissions.find(p => p.module === module) || { can_view: false, can_edit: false, can_delete: false };
  const isFullAccess = (role: string) => FULL_ACCESS_ROLES.includes(role);
  const isProjectScopedFullModules = (role: string) => PROJECT_SCOPED_FULL_MODULES_ROLES.includes(role);

  const getRoleBadgeStyle = (role: string) => {
    const colors = ROLE_BADGE_COLORS[role] || { bg: '#f0f2f5', color: '#6b7280' };
    return { ...styles.roleBadge, backgroundColor: colors.bg, color: colors.color };
  };

  return (
    <div style={styles.container}>
      <AppHeader user={user} onHome={onBack} onProfileEdit={onProfileEdit} onLogout={onLogout} />

      <div style={styles.breadcrumb}>
        <span style={styles.breadcrumbLink} onClick={onBack}>Dashboard</span>
        <span style={styles.breadcrumbSep}> / </span>
        <span style={styles.breadcrumbCurrent}>{L.userManagement.breadcrumbTitle}</span>
      </div>

      <div style={styles.main}>
        <div style={styles.layout}>
          {/* Left panel */}
          <div style={styles.leftPanel}>
            <div style={styles.panelHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h2 style={styles.panelTitle}>{L.userManagement.panelTitle}</h2>
                {userLimit !== null && (
                  <span style={{ fontSize: 12, color: users.length >= userLimit ? '#92400e' : '#6b7280', background: users.length >= userLimit ? '#fff7ed' : '#f3f4f6', border: `1px solid ${users.length >= userLimit ? '#fde68a' : '#e5e7eb'}`, borderRadius: 20, padding: '2px 10px', fontWeight: 600 }}>
                    {users.length}/{userLimit}
                  </span>
                )}
              </div>
              <button
                style={{ ...styles.addBtn, opacity: (userLimit !== null && users.length >= userLimit) ? 0.45 : 1, cursor: (userLimit !== null && users.length >= userLimit) ? 'not-allowed' : 'pointer' }}
                onClick={() => { if (userLimit === null || users.length < userLimit) { setNewUser({ email: '', full_name: '', role: 'coordinator', password: '' }); setEmailCheckResult(null); setInviteProject(''); setPmProjectIds([]); setInviteRole(user.role === 'pm' ? 'coordinator' : 'pm'); setError(''); setShowNewUser(true); } }}
              >{L.userManagement.newUserBtn}</button>
            </div>
            {userLimit !== null && users.length >= userLimit && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
                <span style={{ fontSize: 15, flexShrink: 0 }}>⚠️</span>
                <p style={{ margin: 0, fontSize: 13, color: '#92400e' }}>
                  {L.userManagement.planLimitMsg(userLimit!)}
                </p>
              </div>
            )}
            {loading ? <p style={styles.loading}>{L.userManagement.loading}</p> : (
              <div style={styles.userList}>
                {users.map((u) => (
                  <div key={u.id} style={{ ...styles.userItem, backgroundColor: selectedUser?.id === u.id ? '#ede9fe' : 'white', borderLeft: selectedUser?.id === u.id ? '3px solid #4f46e5' : '3px solid transparent' }}
                    onClick={() => { setSelectedUser(u); setSelectedProject(''); setPermissions([]); setActiveTab('projects'); }}>
                    <div style={styles.userAvatar}>{u.full_name.charAt(0).toUpperCase()}</div>
                    <div style={styles.userInfo}>
                      <p style={styles.userItemName}>{u.full_name}</p>
                      <p style={styles.userItemEmail}>{u.email}</p>
                    </div>
                    <span style={getRoleBadgeStyle(u.role)}>{ROLE_LABELS[u.role] ?? u.role.toUpperCase()}</span>
                    {u.id !== user.id && (
                      <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                        <button style={styles.editBtn} title={L.userManagement.editTip} onClick={(e) => { e.stopPropagation(); openEditModal(u); }}>✏️</button>
                        <button style={styles.deleteBtn} onClick={(e) => { e.stopPropagation(); setDeletingUser(u); }}>✕</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right panel */}
          <div style={styles.rightPanel}>
            {!selectedUser ? (
              <div style={styles.emptyState}>
                <p>{L.userManagement.emptyState}</p>
              </div>
            ) : (
              <>
                <div style={styles.panelHeader}>
                  <h2 style={styles.panelTitle}>{L.userManagement.manageTitle(selectedUser.full_name)}</h2>
                  <span style={getRoleBadgeStyle(selectedUser.role)}>{ROLE_LABELS[selectedUser.role] ?? selectedUser.role.toUpperCase()}</span>
                </div>

                {isFullAccess(selectedUser.role) ? (
                  <div style={{ backgroundColor: '#ede9fe', borderRadius: '10px', padding: '16px 20px', border: '1px solid #c4b5fd' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '20px' }}>✦</span>
                      <div>
                        <p style={{ fontSize: '14px', fontWeight: '600', color: '#4f46e5', margin: 0 }}>Full Access</p>
                        <p style={{ fontSize: '13px', color: '#7c3aed', margin: '4px 0 0 0' }}>
                          {L.userManagement.fullAccessRole(selectedUser.role.toUpperCase())}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Tabs — PM has full module rights automatically, so no "Modules" tab needed */}
                    {!isProjectScopedFullModules(selectedUser.role) && (
                      <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', backgroundColor: '#f3f4f6', borderRadius: '10px', padding: '4px' }}>
                        {[{ id: 'projects', label: L.userManagement.tabProjects }, { id: 'modules', label: L.userManagement.tabModules }].map(tab => (
                          <button key={tab.id} onClick={() => setActiveTab(tab.id as any)}
                            style={{ flex: 1, padding: '8px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '500', backgroundColor: activeTab === tab.id ? 'white' : 'transparent', color: activeTab === tab.id ? '#1a1a2e' : '#9ca3af', boxShadow: activeTab === tab.id ? '0 1px 4px rgba(0,0,0,0.08)' : 'none', transition: 'all 0.15s' }}>
                            {tab.label}
                          </button>
                        ))}
                      </div>
                    )}

                    {activeTab === 'projects' && (
                      <div>
                        <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
                          {isProjectScopedFullModules(selectedUser.role) ? L.userManagement.pmProjectsHint : L.userManagement.projectsDesc}
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {projects.map(p => {
                            const isMember = memberProjects.includes(p.id);
                            return (
                              <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderRadius: '10px', border: `1px solid ${isMember ? '#c4b5fd' : '#e5e7eb'}`, backgroundColor: isMember ? '#faf5ff' : 'white', transition: 'all 0.15s' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                  <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: isMember ? '#8b5cf6' : '#d1d5db' }} />
                                  <span style={{ fontSize: '14px', fontWeight: '500', color: '#1a1a2e' }}>{p.name}</span>
                                </div>
                                <button onClick={() => handleToggleMembership(p.id, isMember)}
                                  style={{ padding: '5px 14px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '600', backgroundColor: isMember ? '#ede9fe' : '#4f46e5', color: isMember ? '#7c3aed' : 'white', transition: 'all 0.15s' }}>
                                  {isMember ? L.userManagement.removeFromProject : L.userManagement.addToProject}
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {activeTab === 'modules' && !isProjectScopedFullModules(selectedUser.role) && (
                      <>
                        <div style={styles.projectSelector}>
                          <label style={styles.label}>{L.userManagement.selectProjectLabel}</label>
                          <select style={styles.select} value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)}>
                            <option value="">{L.userManagement.selectProjectPlaceholder}</option>
                            {projects.filter(p => memberProjects.includes(p.id)).map(p => (
                              <option key={p.id} value={p.id}>{p.name}</option>
                            ))}
                          </select>
                          {memberProjects.length === 0 && (
                            <p style={{ fontSize: '12px', color: '#f59e0b', marginTop: '8px' }}>
                              {L.userManagement.noProjectsWarning}
                            </p>
                          )}
                        </div>
                        {selectedProject && (
                          <div style={styles.permissionsTable}>
                            <div style={styles.tableHeader}>
                              <span style={{ flex: 2 }}>Module</span>
                              <span style={styles.permCol}>View</span>
                              <span style={styles.permCol}>Edit</span>
                              <span style={styles.permCol}>Delete</span>
                            </div>
                            {MODULES.map((module) => {
                              const perm = getPermission(module);
                              return (
                                <div key={module} style={styles.tableRow}>
                                  <span style={{ flex: 2, fontSize: '14px', color: '#374151' }}>{MODULE_DISPLAY_LABELS[module] || module}</span>
                                  {(['can_view', 'can_edit', 'can_delete'] as const).map((type) => (
                                    <span key={type} style={styles.permCol}>
                                      <input type="checkbox" checked={perm[type]} onChange={(e) => handlePermissionChange(module, type, e.target.checked)} style={styles.checkbox} />
                                    </span>
                                  ))}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* New User Modal */}
      {showNewUser && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>{L.userManagement.newUserTitle}</h2>

            {/* Email field — always shown, with onBlur check */}
            <div style={styles.field}>
              <label style={styles.label}>{L.userManagement.emailLabel}</label>
              <div style={{ position: 'relative' }}>
                <input
                  style={styles.input}
                  type="email"
                  autoComplete="off"
                  value={newUser.email}
                  onChange={(e) => { setNewUser({ ...newUser, email: e.target.value }); setEmailCheckResult(null); }}
                  onBlur={(e) => { if (e.target.value !== newUser.email) setNewUser({ ...newUser, email: e.target.value }); checkEmail(e.target.value); }}
                  placeholder="user@example.com"
                />
                {emailChecking && (
                  <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: '#9ca3af' }}>{L.userManagement.checking}</span>
                )}
              </div>
            </div>

            {/* User already in org — show error */}
            {emailCheckResult?.already_in_org && (
              <>
                <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px' }}>
                  <p style={{ margin: 0, fontSize: '13px', color: '#dc2626', fontWeight: '500' }}>
                    {L.userManagement.alreadyInOrg}
                  </p>
                </div>
                <div style={styles.modalButtons}>
                  <button style={styles.cancelBtn} onClick={() => { setShowNewUser(false); setError(''); setEmailCheckResult(null); }}>{L.userManagement.close}</button>
                </div>
              </>
            )}

            {/* User found in system but not in this org — invite flow */}
            {emailCheckResult?.exists && !emailCheckResult?.already_in_org && (
              <>
                <div style={{ backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px' }}>
                  <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#166534', fontWeight: '600' }}>
                    {L.userManagement.userFoundTitle(emailCheckResult.user?.full_name)}
                  </p>
                  <p style={{ margin: 0, fontSize: '13px', color: '#16a34a' }}>
                    {L.userManagement.userFoundDesc}
                  </p>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>{L.userManagement.orgRoleLabel}</label>
                  <select style={styles.select} value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
                    {inviteRoleOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                {inviteRole === 'pm' ? (
                  <div style={styles.field}>
                    <label style={styles.label}>{L.userManagement.pmProjectsLabel}</label>
                    <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 8px' }}>{L.userManagement.pmProjectsHint}</p>
                    <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
                      {projects.filter(p => p.status !== 'archived').map(p => (
                        <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', fontSize: 13, cursor: 'pointer' }}>
                          <input type="checkbox" checked={pmProjectIds.includes(p.id)}
                            onChange={(e) => setPmProjectIds(prev => e.target.checked ? [...prev, p.id] : prev.filter(id => id !== p.id))} />
                          {p.name}
                        </label>
                      ))}
                      {projects.filter(p => p.status !== 'archived').length === 0 && (
                        <p style={{ fontSize: 12, color: '#9ca3af', margin: 4 }}>{L.userManagement.pmProjectsEmpty}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={styles.field}>
                    <label style={styles.label}>{L.userManagement.accessProjectLabel} <span style={{ fontWeight: 400, color: '#9ca3af' }}>{L.userManagement.accessProjectOptional}</span></label>
                    <select style={styles.select} value={inviteProject} onChange={(e) => setInviteProject(e.target.value)}>
                      <option value="">{L.userManagement.noProjectOption}</option>
                      {projects.filter(p => p.status !== 'archived').map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                )}
                {error && <p style={styles.error}>{error}</p>}
                <div style={styles.modalButtons}>
                  <button style={styles.cancelBtn} onClick={() => { setShowNewUser(false); setError(''); setEmailCheckResult(null); setInviteProject(''); setPmProjectIds([]); }}>{L.userManagement.cancel}</button>
                  <button style={styles.createBtn} onClick={handleInviteUser} disabled={creating}>{creating ? L.userManagement.inviting : L.userManagement.invite}</button>
                </div>
              </>
            )}

            {/* Normal create flow — user not found in system */}
            {!emailCheckResult?.exists && (
              <>
                <div style={styles.field}><label style={styles.label}>{L.userManagement.nameLabel}</label><input style={styles.input} type="text" autoComplete="off" value={newUser.full_name} onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })} placeholder={L.userManagement.namePlaceholder} /></div>
                <div style={styles.field}><label style={styles.label}>{L.userManagement.passwordLabel}</label><input style={styles.input} type="password" autoComplete="new-password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="••••••••" /></div>
                <div style={styles.field}>
                  <label style={styles.label}>{L.userManagement.roleLabel}</label>
                  <select style={styles.select} value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
                    {inviteRoleOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                {newUser.role === 'pm' && (
                  <div style={styles.field}>
                    <label style={styles.label}>{L.userManagement.pmProjectsLabel}</label>
                    <p style={{ fontSize: '12px', color: '#9ca3af', margin: '0 0 8px' }}>{L.userManagement.pmProjectsHint}</p>
                    <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
                      {projects.filter(p => p.status !== 'archived').map(p => (
                        <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', fontSize: 13, cursor: 'pointer' }}>
                          <input type="checkbox" checked={pmProjectIds.includes(p.id)}
                            onChange={(e) => setPmProjectIds(prev => e.target.checked ? [...prev, p.id] : prev.filter(id => id !== p.id))} />
                          {p.name}
                        </label>
                      ))}
                      {projects.filter(p => p.status !== 'archived').length === 0 && (
                        <p style={{ fontSize: 12, color: '#9ca3af', margin: 4 }}>{L.userManagement.pmProjectsEmpty}</p>
                      )}
                    </div>
                  </div>
                )}
                {error && <p style={styles.error}>{error}</p>}
                <p style={styles.emailNote}>{L.userManagement.emailNote}</p>
                <div style={styles.modalButtons}>
                  <button style={styles.cancelBtn} onClick={() => { setShowNewUser(false); setError(''); setEmailCheckResult(null); setPmProjectIds([]); }}>{L.userManagement.cancel}</button>
                  <button style={styles.createBtn} onClick={handleCreateUser} disabled={creating}>{creating ? L.userManagement.creating : L.userManagement.create}</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <h2 style={styles.modalTitle}>{L.userManagement.editTitle}</h2>
            <div style={styles.field}><label style={styles.label}>{L.userManagement.nameLabel}</label><input style={styles.input} type="text" value={editForm.full_name} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} /></div>
            <div style={styles.field}><label style={styles.label}>{L.userManagement.emailLabel}</label><input style={styles.input} type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
            <div style={styles.field}>
              <label style={styles.label}>{L.userManagement.roleLabel}</label>
              <select style={styles.select} value={editForm.role} onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}>
                {ROLE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            {editError && <p style={styles.error}>{editError}</p>}
            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn} onClick={() => { setEditingUser(null); setEditError(''); }}>{L.userManagement.cancel}</button>
              <button style={styles.createBtn} onClick={handleUpdateUser} disabled={saving}>{saving ? L.userManagement.saving : L.userManagement.save}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deletingUser && (
        <div style={styles.overlay}>
          <div style={{ ...styles.modal, width: '420px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: '20px' }}>🗑️</span>
              </div>
              <div>
                <h2 style={{ ...styles.modalTitle, marginBottom: '4px' }}>{L.userManagement.deleteTitle}</h2>
                <p style={{ fontSize: '12px', color: '#9ca3af', margin: 0 }}>{L.userManagement.deleteIrreversible}</p>
              </div>
            </div>
            <div style={{ backgroundColor: '#f9fafb', borderRadius: '10px', padding: '14px 16px', marginBottom: '16px', border: '1px solid #f3f4f6' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#4f46e5', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '700', flexShrink: 0 }}>
                  {deletingUser.full_name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a2e' }}>{deletingUser.full_name}</div>
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>{deletingUser.email}</div>
                </div>
              </div>
            </div>
            <div style={{ backgroundColor: '#fef2f2', borderRadius: '8px', padding: '12px 14px', marginBottom: '24px', border: '1px solid #fecaca', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
              <span style={{ fontSize: '14px', flexShrink: 0 }}>⚠️</span>
              <p style={{ fontSize: '13px', color: '#dc2626', margin: 0, lineHeight: 1.5 }}>{L.userManagement.deleteWarning}</p>
            </div>
            <div style={styles.modalButtons}>
              <button style={styles.cancelBtn} onClick={() => setDeletingUser(null)}>{L.userManagement.cancel}</button>
              <button style={{ backgroundColor: '#ef4444', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', opacity: deleting ? 0.6 : 1 }} onClick={handleDeleteUser} disabled={deleting}>
                {deleting ? L.userManagement.deleting : L.userManagement.deleteBtn}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { minHeight: '100vh', backgroundColor: '#f0f2f5' },
  header: { backgroundColor: '#1a1a2e', padding: '0 32px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo: { color: 'white', fontSize: '22px', fontWeight: 'bold', margin: 0 },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  userName: { color: 'white', fontSize: '14px' },
  userRole: { backgroundColor: '#4f46e5', color: 'white', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' },
  breadcrumb: { padding: '16px 32px', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', fontSize: '14px' },
  breadcrumbLink: { color: '#4f46e5', cursor: 'pointer', fontWeight: '500' },
  breadcrumbSep: { color: '#9ca3af', margin: '0 8px' },
  breadcrumbCurrent: { color: '#374151', fontWeight: '500' },
  main: { padding: '32px', maxWidth: '1400px', margin: '0 auto' },
  layout: { display: 'grid', gridTemplateColumns: '340px 1fr', gap: '24px' },
  leftPanel: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  rightPanel: { backgroundColor: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  panelHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' },
  panelTitle: { fontSize: '18px', fontWeight: '600', color: '#1a1a2e', margin: 0 },
  addBtn: { backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
  loading: { color: '#666' },
  userList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  userItem: { display: 'flex', alignItems: 'center', gap: '12px', padding: '12px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.15s' },
  userAvatar: { width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#4f46e5', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '600', flexShrink: 0 },
  userInfo: { flex: 1, minWidth: 0 },
  userItemName: { fontSize: '14px', fontWeight: '500', color: '#1a1a2e', margin: 0 },
  userItemEmail: { fontSize: '12px', color: '#9ca3af', margin: '2px 0 0 0' },
  roleBadge: { padding: '3px 8px', borderRadius: '12px', fontSize: '10px', fontWeight: '600', flexShrink: 0 },
  editBtn: { backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '4px' },
  deleteBtn: { backgroundColor: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '14px', padding: '4px' },
  emptyState: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px', color: '#9ca3af', fontSize: '14px', textAlign: 'center' },
  projectSelector: { marginBottom: '24px' },
  label: { display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333', fontSize: '14px' },
  select: { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', outline: 'none', backgroundColor: 'white' },
  permissionsTable: { borderRadius: '8px', overflow: 'hidden', border: '1px solid #e5e7eb' },
  tableHeader: { display: 'flex', alignItems: 'center', padding: '12px 16px', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' as const },
  tableRow: { display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #f3f4f6' },
  permCol: { width: '80px', textAlign: 'center' as const, flexShrink: 0 },
  checkbox: { width: '16px', height: '16px', cursor: 'pointer' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: 'white', borderRadius: '12px', padding: '32px', width: '480px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  modalTitle: { fontSize: '20px', fontWeight: '600', color: '#1a1a2e', marginBottom: '0' },
  field: { marginBottom: '20px' },
  input: { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box' as const, outline: 'none', fontFamily: 'inherit' },
  error: { color: '#ef4444', fontSize: '14px', marginBottom: '12px' },
  emailNote: { color: '#6b7280', fontSize: '13px', marginBottom: '16px', margin: '0 0 16px 0' },
  modalButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '8px' },
  cancelBtn: { backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },
  createBtn: { backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
};

export default UserManagement;
